import React, { useEffect } from 'react'
import { useRouter } from 'next/router'
import MilliplexStep1 from '../Step1'
import MilliplexStep2 from '../Step2/Step2'
import MilliplexStep3 from '../Step3/Step3'
import { ActiveLinkType } from '../enum'
import { MilliplexFormValues } from '../Milliplex'
import { useFormikContext } from 'formik'
import { isEmpty } from 'lodash'
import { useMilliplexSearchCriteriaListLazyQuery } from '@src/queries/MilliplexSearchCriteriaListQuery.generated'
import { PlateTypeWithStatus } from '../utils'

const MilliplexContentComponent: React.FC<{
  displayPageError: boolean
  setDisableSelectKitsLink: React.Dispatch<React.SetStateAction<boolean>>
  setDisableReviewAndOrderLink: React.Dispatch<React.SetStateAction<boolean>>
  setDisplayPageError: React.Dispatch<React.SetStateAction<boolean>>
  setDisableSearchCriteria: React.Dispatch<React.SetStateAction<boolean>>
}> = ({
  displayPageError,
  setDisableSelectKitsLink,
  setDisableReviewAndOrderLink,
  setDisplayPageError,
  setDisableSearchCriteria,
}) => {
  const router = useRouter()
  const {
    activeLink,
    analytes: analytesFromRouter,
    species: speciesFromRouter,
    researchAreas: researchAreasFromRouter,
    previouslyViewed: previouslyViewedFromRouter,
    assay: assayFromRouter,
    plateType: plateTypeFromRouter,
    instrumentType: instrumentTypeFromRouter,
  } = router.query
  const formik = useFormikContext<MilliplexFormValues>()
  const step3Valid = !isEmpty(formik.values.selectedKits)
  const itIsSharedContent = decodeURIComponent(router.asPath).includes(
    'sharedContent="true"'
  )

  const step2Valid =
    !isEmpty(speciesFromRouter) ||
    !isEmpty(formik.values.species) ||
    !isEmpty(previouslyViewedFromRouter)

  const [
    getMilliplexSearchCriteriaList,
    { data: searchCriteriaList, loading: loadingSearchCriteriaList },
  ] = useMilliplexSearchCriteriaListLazyQuery()

  useEffect(() => {
    getMilliplexSearchCriteriaList()
  }, [])

  useEffect(() => {
    setDisableSelectKitsLink(!step2Valid)
  }, [step2Valid])

  useEffect(() => {
    setDisableReviewAndOrderLink(!step3Valid)
  }, [step3Valid, step2Valid])

  useEffect(() => {
    if (!isEmpty(speciesFromRouter)) {
      formik.setFieldValue('species', speciesFromRouter)
    }
    if (!isEmpty(researchAreasFromRouter)) {
      formik.setFieldValue(
        'researchAreas',
        Array.isArray(researchAreasFromRouter)
          ? researchAreasFromRouter
          : [researchAreasFromRouter]
      )
    }
    if (!isEmpty(analytesFromRouter)) {
      formik.setFieldValue(
        'analytes',
        Array.isArray(analytesFromRouter)
          ? analytesFromRouter
          : [analytesFromRouter]
      )
    }
    if (!isEmpty(assayFromRouter)) {
      formik.setFieldValue('assay', assayFromRouter)
    }
    if (!isEmpty(plateTypeFromRouter)) {
      formik.setFieldValue('plateType', plateTypeFromRouter)
    }
    if (!isEmpty(instrumentTypeFromRouter)) {
      formik.setFieldValue(
        'instrumentType',
        Array.isArray(instrumentTypeFromRouter)
          ? instrumentTypeFromRouter
          : [instrumentTypeFromRouter]
      )
    }
    if (!isEmpty(previouslyViewedFromRouter)) {
      formik.setFieldValue('previouslyViewed', previouslyViewedFromRouter)
    }
  }, [])

  useEffect(() => {
    if (itIsSharedContent) {
      setDisableSearchCriteria(itIsSharedContent)
      setDisableSelectKitsLink(itIsSharedContent)
    }
  }, [itIsSharedContent])

  const speciesList =
    searchCriteriaList?.getMilliplexSearchCriteriaList?.listSpeciesResearchAreas
      .map((single) => single.species)
      .sort() || []

  const listResearchAreas =
    searchCriteriaList?.getMilliplexSearchCriteriaList
      ?.listSpeciesResearchAreas || []
  const assayTypeList =
    searchCriteriaList?.getMilliplexSearchCriteriaList?.assayTypePlateType || []

  const plateTypeForAssay =
    searchCriteriaList?.getMilliplexSearchCriteriaList?.assayTypePlateType.find(
      (assay) => assay.assayType === assayFromRouter
    )?.plateType || []

  const plateTypeList =
    searchCriteriaList?.getMilliplexSearchCriteriaList?.assayTypePlateType
      .flatMap((single) => single.plateType)
      .filter((plate, index, list) => list.indexOf(plate) === index)

  const plateTypeListWithStatus: PlateTypeWithStatus[] =
    plateTypeList?.map((plate) => ({
      plateType: plate,
      enabled: plateTypeForAssay.includes(plate),
    })) || []

  const instrumentType =
    searchCriteriaList?.getMilliplexSearchCriteriaList?.plateInstrumentType ||
    []

  const redirectToStep1 = () => {
    delete router.query.activeLink
    router.replace(router)
    return (
      <MilliplexStep1
        displayPageError={displayPageError}
        setDisplayPageError={setDisplayPageError}
        speciesList={speciesList}
        listResearchAreas={listResearchAreas}
        assayTypeList={assayTypeList}
        loadingSearchCriteriaList={loadingSearchCriteriaList}
      />
    )
  }

  const MilliplexContentComponent = () => {
    switch (activeLink) {
      case ActiveLinkType.SelectKits:
        if (!step2Valid) {
          redirectToStep1()
        }
        return (
          <MilliplexStep2
            displayPageError={displayPageError}
            setDisplayPageError={setDisplayPageError}
            plateType={plateTypeListWithStatus}
            instrumentType={instrumentType}
          />
        )
      case ActiveLinkType.ReviewAndOrder:
        return (
          <MilliplexStep3 setDisableSearchCriteria={setDisableSearchCriteria} />
        )
      default:
        return (
          <MilliplexStep1
            displayPageError={displayPageError}
            setDisplayPageError={setDisplayPageError}
            speciesList={speciesList}
            listResearchAreas={listResearchAreas}
            assayTypeList={assayTypeList}
            loadingSearchCriteriaList={loadingSearchCriteriaList}
          />
        )
    }
  }

  return <>{MilliplexContentComponent()}</>
}
export default MilliplexContentComponent
